/**
 * Use Case: Fetch Feed
 * 
 * Orchestrates fetching the user feed from the media service.
 * Part of clean architecture: Presentation → UseCase → Service
 */

import { MediaService } from '@/domain/services/media/mediaService';
import type { UserMediaEntity } from '@/domain/entities/userMedia/userMediaEntity';
import { appLogger } from '@/domain/services/analytics/appLogger';

export class FetchFeedUseCase {
    private mediaService: MediaService;

    /**
     * Constructor with Dependency Injection
     * @param mediaService - Injected MediaService instance
     */
    constructor(mediaService: MediaService = new MediaService()) {
        this.mediaService = mediaService;
    }

    /**
     * Execute the use case - fetch feed
     * Returns UserMediaEntity[] (domain entities)
     */
    async execute(): Promise<{ medias: UserMediaEntity[]; hasMore: boolean }> {
        try {
            // MediaService handles:
            // 1. Fetching raw data from CloudFunctionsService
            // 2. Mapping DTOs to Entities via remapper
            return await this.mediaService.fetchFeed();
        } catch (error) {
            appLogger.error('FetchFeedUseCase error', 'FetchFeedUseCase.execute', { error: error as Error });
            return {
                medias: [],
                hasMore: false
            };
        }
    }
}

/**
 * Factory function to create a new FetchFeedUseCase instance
 * Use this in presentation layer instead of singleton
 * 
 * @example
 * // In Redux thunk
 * const useCase = createFetchFeedUseCase();
 * const result = await useCase.execute();
 */
export const createFetchFeedUseCase = (): FetchFeedUseCase => {
    return new FetchFeedUseCase();
};
